import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { type FormEvent, useEffect, useState } from "react"
import { AuthLayout } from "@/components/auth-layout"
import { ErrorText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Field } from "@/components/ui/field"
import { authClient, authCode, unwrap } from "@/lib/auth-client"
import { resetTokenCheckQuery } from "@/lib/email-api"
import { refreshSession } from "@/lib/queries"

/** The shape of a reset token in the email's link; anything else is dropped from the address before it is read. */
export const RESET_TOKEN = /^[A-Za-z0-9_-]{8,128}$/

/**
 * The token of the reset link, held in memory only (wave M spec §6.5). The route takes it out of the address before
 * the page renders (its `beforeLoad` holds it here and redirects to `/reset-password` with `replace`), so it is never
 * left in the address bar, the history or a copied URL; the page copies it into its state. A reload starts without
 * it and shows "expired or already used" (the email's link still works until it is used).
 */
let heldToken: string | null = null
export const holdResetToken = (token: string) => {
  heldToken = token
}
export const heldResetToken = (): string | null => heldToken
export const dropResetToken = () => {
  heldToken = null
}

/** POST /api/auth/reset-password with the token from memory. Throws `AuthCallError` with Better Auth's code. */
export const setNewPassword = async (token: string, newPassword: string) =>
  unwrap(await authClient.resetPassword({ newPassword, token }))

/** Better Auth's refusal in words; null for an expired or used link (the page shows its own state for that). */
export const resetErrorText = (error: unknown): string | null => {
  switch (authCode(error)) {
    case "INVALID_TOKEN":
      return null
    case "PASSWORD_TOO_SHORT":
      return "Use at least 10 characters."
    case "PASSWORD_TOO_LONG":
      return "That password is too long."
    default:
      return "That didn't work. Try again."
  }
}

const linkButton =
  "inline-flex h-10 items-center justify-center rounded-control px-4 text-sm font-semibold no-underline"

/**
 * `/reset-password` (wave M spec §6.5): choose a new password with the emailed link. Works signed in and out. The
 * token is checked first, so a used or expired link says so before anyone types a password. If the check can't be
 * made (offline), the form shows and the submit says.
 */
export function ResetPasswordPage() {
  const queryClient = useQueryClient()
  const [token] = useState(heldResetToken)
  const [state, setState] = useState<"form" | "done" | "invalid">(token ? "form" : "invalid")
  const check = useQuery({
    ...resetTokenCheckQuery(token ?? ""),
    enabled: token !== null && state === "form",
  })
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  useEffect(() => () => dropResetToken(), [])

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!token || password.length < 10) return
    setPending(true)
    setError(null)
    try {
      await setNewPassword(token, password)
      dropResetToken()
      setState("done")
      // Every session ended with the reset, this one too.
      await refreshSession(queryClient).catch(() => null)
    } catch (failure) {
      const text = resetErrorText(failure)
      if (text) setError(text)
      else setState("invalid")
    } finally {
      setPending(false)
      setPassword("")
    }
  }

  if (state === "done")
    return (
      <AuthLayout
        title="Password changed"
        description="You're signed out everywhere. Sign in with your new password."
        footer={null}
      >
        <Link
          to="/sign-in"
          className={`${linkButton} w-full bg-ink text-white hover:bg-ink/88 hover:text-white`}
        >
          Sign in
        </Link>
      </AuthLayout>
    )

  if (state === "invalid" || !token || check.data?.valid === false)
    return (
      <AuthLayout
        title="Choose a new password"
        description="This link has expired or was already used."
        footer={
          <Link to="/sign-in" className="font-medium link">
            Back to sign in
          </Link>
        }
      >
        <Link
          to="/forgot-password"
          className={`${linkButton} w-full border border-line-control text-ink hover:bg-fill hover:text-ink`}
        >
          Ask for a new link
        </Link>
      </AuthLayout>
    )

  if (check.isPending)
    return (
      <AuthLayout title="Choose a new password" description="Checking your link…" footer={null}>
        <span className="sr-only" role="status">
          Checking your link
        </span>
      </AuthLayout>
    )

  return (
    <AuthLayout
      title="Choose a new password"
      description="Setting it signs you out everywhere."
      footer={
        <Link to="/sign-in" className="font-medium link">
          Back to sign in
        </Link>
      }
    >
      <form className="grid gap-4" onSubmit={onSubmit}>
        <div className="grid gap-1.5">
          <Field
            id="new-password"
            label="New password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="text-xs text-muted">At least 10 characters.</span>
        </div>
        {error && <ErrorText>{error}</ErrorText>}
        <Button type="submit" disabled={pending || password.length < 10}>
          {pending ? "Setting…" : "Set password"}
        </Button>
      </form>
    </AuthLayout>
  )
}
